import React, { useEffect, useMemo, useRef, useState } from "react";
import Lottie from "lottie-react";
import CourseSwitcher from "./components/CourseSwitcher";
import DeepDiveWorkspace from "./components/DeepDiveWorkspace";
import GuidedTour from "./components/GuidedTour";
import LoginModal from "./components/LoginModal";
import WelcomeModal from "./components/WelcomeModal";
import CalendarPage from "./pages/CalendarPage";
import UnitsPage from "./pages/UnitsPage";
import TeksPage from "./pages/TeksPage";
import SettingsPage from "./pages/SettingsPage";
import CurriculumPage from "./pages/CurriculumPage";
import GuidePage from "./pages/GuidePage";
import LessonLibraryPage from "./pages/LessonLibraryPage";
import KioskControlPage from "./pages/KioskControlPage";
import KioskPage from "./pages/KioskPage";
import PerformanceTasksPage from "./pages/PerformanceTasksPage";
import CurriculumAuditPage from "./pages/CurriculumAuditPage";
import PlanningHubPage from "./pages/PlanningHubPage";
import WalkthroughPage from "./pages/WalkthroughPage";
import UserManagementPage from "./pages/UserManagementPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProfilePage from "./pages/ProfilePage";
import appVersion from "./appVersion.json";
import meditatingGiraffeAnimation from "./assets/lottie/meditating-giraffe.json";
import signInArrowAnimation from "./assets/lottie/sign-in-arrow.json";
import {
  clearAuthSession,
  getAuthSession,
  sessionStorageKeys,
  setAuthSession,
  teacherAPI,
  walkthroughAPI,
} from "./services/api";
import {
  dismissObservation,
  getDismissedObservationIds,
  setDismissedObservationIds,
} from "./utils/observationNotifications";
import "./App.css";

const getRouteFromHash = () => {
  const planningTabs = new Set([
    "week",
    "objectives",
    "lessons",
    "strategies",
    "observations",
  ]);
  const hash = window.location.hash.replace(/^#/, "");
  const normalized = hash || "/calendar";
  const [pathOnlyRaw] = normalized.split("?");
  const pathOnly = pathOnlyRaw.split("#")[0];
  const parts = pathOnly.split("/").filter(Boolean);

  if (
    /(^|[?#&])access_token=/.test(normalized) &&
    (!parts.length || String(parts[0]).includes("="))
  ) {
    return { name: "reset-password", param: null };
  }

  if (parts[0] === "lesson" && parts[1]) {
    return { name: "lesson", param: parts[1] };
  }
  if (parts[0] === "performance-tasks") {
    return { name: "performance-tasks", param: parts[1] || null };
  }
  if (parts[0] === "kiosk") {
    return { name: "kiosk", param: parts[1] || null };
  }
  if (parts[0] === "planning") {
    const tab = planningTabs.has(parts[1]) ? parts[1] : "week";
    const redirectPath =
      parts[1] && planningTabs.has(parts[1]) ? null : "/planning/week";
    return { name: "planning", param: tab, redirectPath };
  }
  if (parts[0] === "planner") {
    return { name: "planning", param: "week", redirectPath: "/planning/week" };
  }
  if (parts[0] === "objective-library") {
    return {
      name: "planning",
      param: "objectives",
      redirectPath: "/planning/objectives",
    };
  }

  const allowed = new Set([
    "calendar",
    "dashboard",
    "units",
    "planning",
    "kiosk-control",
    "guide",
    "lessons",
    "teks",
    "settings",
    "curriculum",
    "kiosk",
    "performance-tasks",
    "curriculum-audit",
    "walkthrough",
    "user-management",
    "profile",
    "forgot-password",
    "reset-password",
  ]);
  const routeName = allowed.has(parts[0]) ? parts[0] : "calendar";
  const name = routeName === "dashboard" ? "calendar" : routeName;
  return { name, param: null, redirectPath: null };
};

const getPathFromHash = () => {
  const hash = window.location.hash.replace(/^#/, "");
  const normalized = hash || "/calendar";
  const [pathOnlyRaw] = normalized.split("?");
  return pathOnlyRaw.split("#")[0] || "/calendar";
};

const WELCOME_SEEN_PREFIX = "curriculum_planner_welcome_seen_v1";
const SIGN_IN_NUDGE_SEEN_KEY = "curriculum_planner_sign_in_nudge_seen_v1";
const SIGN_IN_CHEVRON_SESSION_KEY =
  "curriculum_planner_sign_in_chevron_shown_v1";

const getWelcomeSeenKey = (profile) => {
  const identity = profile?.userId || profile?.email;
  if (!identity) return null;
  return `${WELCOME_SEEN_PREFIX}:${String(identity).trim().toLowerCase()}`;
};

const hasObservationNote = (observation = {}) =>
  Boolean(String(observation.notes || "").trim());

const toObservationDateLabel = (dateValue) => {
  const raw = String(dateValue || "").trim();
  if (!raw) return "Unknown date";
  const dateOnly = raw.includes("T") ? raw.split("T")[0] : raw;
  const parsed = new Date(`${dateOnly}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateOnly;
  return parsed.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

function App() {
  const [currentCourse, setCurrentCourse] = useState(null);
  const [kioskCourseSelection, setKioskCourseSelection] = useState(null);
  const [route, setRoute] = useState(getRouteFromHash());
  const [lastNonLessonPath, setLastNonLessonPath] = useState("/calendar");
  const [authSession, setAuthSessionState] = useState(() => getAuthSession());
  const [authProfile, setAuthProfile] = useState(null);
  const [authError, setAuthError] = useState("");
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const [showSignInNudge, setShowSignInNudge] = useState(false);
  const [showSignInChevron, setShowSignInChevron] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [observationInboxOpen, setObservationInboxOpen] = useState(false);
  const [observationInboxLoading, setObservationInboxLoading] = useState(false);
  const [observationInboxError, setObservationInboxError] = useState("");
  const [observationRows, setObservationRows] = useState([]);
  const [dismissedObservationIds, setDismissedObservationIdsState] = useState(
    new Set(),
  );
  const adminDropdownRef = useRef(null);

  useEffect(() => {
    const currentHash = window.location.hash.replace(/^#/, "");
    if (!window.location.hash || currentHash === "/dashboard") {
      window.location.hash = "/calendar";
    }
    const onHashChange = () => setRoute(getRouteFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncAuthSession = () => {
      const session = getAuthSession();
      setAuthSessionState(session);
      if (!session?.accessToken) {
        setAuthProfile(null);
      }
    };

    window.addEventListener(
      sessionStorageKeys.AUTH_SESSION_CHANGED_EVENT,
      syncAuthSession,
    );
    window.addEventListener("storage", syncAuthSession);
    return () => {
      window.removeEventListener(
        sessionStorageKeys.AUTH_SESSION_CHANGED_EVENT,
        syncAuthSession,
      );
      window.removeEventListener("storage", syncAuthSession);
    };
  }, []);

  useEffect(() => {
    if (route.name !== "lesson" && route.name !== "kiosk") {
      setLastNonLessonPath(getPathFromHash());
    }
  }, [route.name, route.param]);

  useEffect(() => {
    if (!route.redirectPath) return;
    const current = window.location.hash.replace(/^#/, "");
    if (current !== route.redirectPath) {
      window.location.hash = route.redirectPath;
    }
  }, [route.redirectPath]);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (!authSession?.accessToken) {
        setAuthProfile(null);
        return;
      }

      try {
        const response = await teacherAPI.getMe();
        if (cancelled) return;
        setAuthProfile(response.data || null);
        setAuthError("");
      } catch (_error) {
        if (cancelled) return;
        clearAuthSession();
        setAuthSessionState(null);
        setAuthProfile(null);
        setAuthError("Session expired. Sign in again.");
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [authSession?.accessToken]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!authSession?.accessToken) {
      setIsWelcomeModalOpen(false);
      return;
    }

    const seenKey = getWelcomeSeenKey(authProfile);
    if (!seenKey) return;

    const hasSeen = window.localStorage.getItem(seenKey) === "1";
    if (!hasSeen) {
      setIsWelcomeModalOpen(true);
    }
  }, [authProfile, authSession?.accessToken]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (authSession?.accessToken) {
      setShowSignInNudge(false);
      return;
    }

    const hasSeenNudge =
      window.localStorage.getItem(SIGN_IN_NUDGE_SEEN_KEY) === "1";
    setShowSignInNudge(!hasSeenNudge);
  }, [authSession?.accessToken]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (authSession?.accessToken) {
      setShowSignInChevron(false);
      return;
    }

    const alreadyShownThisSession =
      window.sessionStorage.getItem(SIGN_IN_CHEVRON_SESSION_KEY) === "1";
    if (alreadyShownThisSession) {
      setShowSignInChevron(false);
      return;
    }

    window.sessionStorage.setItem(SIGN_IN_CHEVRON_SESSION_KEY, "1");
    setShowSignInChevron(true);
    const timeoutId = window.setTimeout(() => {
      setShowSignInChevron(false);
    }, 20_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authSession?.accessToken]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        adminDropdownRef.current &&
        !adminDropdownRef.current.contains(e.target)
      ) {
        setAdminDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const role = authProfile?.role;
    const userId = authProfile?.userId || authProfile?.id || "";
    if (role !== "teacher" || !userId) {
      setDismissedObservationIdsState(new Set());
      setObservationRows([]);
      setObservationInboxError("");
      setObservationInboxOpen(false);
      return;
    }
    setDismissedObservationIdsState(getDismissedObservationIds(userId));
  }, [authProfile?.id, authProfile?.role, authProfile?.userId]);

  useEffect(() => {
    const role = authProfile?.role;
    if (!authSession?.accessToken || role !== "teacher") {
      setObservationRows([]);
      setObservationInboxLoading(false);
      setObservationInboxError("");
      return undefined;
    }

    let cancelled = false;
    const loadSharedObservations = async () => {
      setObservationInboxLoading(true);
      setObservationInboxError("");
      try {
        const response = await walkthroughAPI.getMySharedObservations();
        if (cancelled) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        setObservationRows(rows.filter(hasObservationNote));
      } catch (error) {
        if (cancelled) return;
        const apiError = String(error?.response?.data?.error || "").trim();
        setObservationInboxError(
          apiError || "Could not load shared observation notes.",
        );
        setObservationRows([]);
      } finally {
        if (!cancelled) setObservationInboxLoading(false);
      }
    };

    loadSharedObservations();
    const intervalId = window.setInterval(loadSharedObservations, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [authSession?.accessToken, authProfile?.role]);

  const navigate = (path) => {
    window.location.hash = path;
  };

  const activeCourseSelection =
    route.name === "kiosk-control" ? kioskCourseSelection : currentCourse;
  const setActiveCourseSelection =
    route.name === "kiosk-control" ? setKioskCourseSelection : setCurrentCourse;

  const handleLoginSuccess = (sessionPayload) => {
    setAuthSession(sessionPayload || null);
    setAuthSessionState(getAuthSession());
    setAuthError("");
  };

  const handleLogout = () => {
    clearAuthSession();
    setAuthSessionState(null);
    setAuthProfile(null);
    setAuthError("");
    setIsWelcomeModalOpen(false);
  };

  const openLoginModal = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SIGN_IN_NUDGE_SEEN_KEY, "1");
    }
    setShowSignInNudge(false);
    setShowSignInChevron(false);
    setIsLoginModalOpen(true);
  };

  const openForgotPasswordPage = () => {
    setIsLoginModalOpen(false);
    setShowSignInNudge(false);
    setShowSignInChevron(false);
    navigate("/forgot-password");
  };

  const handlePasswordResetComplete = (sessionPayload) => {
    if (sessionPayload?.accessToken) {
      setAuthSession(sessionPayload);
      setAuthSessionState(getAuthSession());
      setAuthError("");
      navigate("/calendar");
      return;
    }

    navigate("/calendar");
    openLoginModal();
  };

  const closeWelcomeModal = () => {
    if (typeof window !== "undefined") {
      const seenKey = getWelcomeSeenKey(authProfile);
      if (seenKey) {
        window.localStorage.setItem(seenKey, "1");
      }
    }
    setIsWelcomeModalOpen(false);
  };

  const pageTitle = useMemo(() => {
    const isAdmin = authProfile?.role === "admin";
    if (route.name === "lesson") return "Lesson";
    if (route.name === "units") return "Units";
    if (route.name === "planning") return "Planning Hub";
    if (route.name === "kiosk-control") return "Kiosk Control";
    if (route.name === "guide") return "Guide";
    if (route.name === "lessons") return "Lessons";
    if (route.name === "teks") return "TEKS";
    if (route.name === "settings")
      return isAdmin ? "Admin" : "Access Restricted";
    if (route.name === "curriculum")
      return isAdmin ? "Curriculum Hub" : "Access Restricted";
    if (route.name === "performance-tasks") return "Performance Tasks";
    if (route.name === "curriculum-audit") return "Curriculum Audit";
    if (route.name === "walkthrough") return "Walkthrough";
    if (route.name === "user-management")
      return isAdmin ? "User Management" : "Access Restricted";
    if (route.name === "profile") return "Profile";
    if (route.name === "forgot-password") return "Forgot Password";
    if (route.name === "reset-password") return "Reset Password";
    if (route.name === "kiosk") return "Kiosk";
    return "Calendar";
  }, [route.name, authProfile?.role]);

  const isAdmin = authProfile?.role === "admin";
  const isPrincipal = authProfile?.role === "principal";
  const isTeacher = authProfile?.role === "teacher";
  const isPrincipalOrAdmin = isAdmin || isPrincipal;
  const isRecoveryRoute =
    route.name === "forgot-password" || route.name === "reset-password";
  const teacherUserId = authProfile?.userId || authProfile?.id || "";

  const unreadObservationRows = useMemo(
    () =>
      observationRows.filter(
        (row) => !dismissedObservationIds.has(String(row.id || "")),
      ),
    [dismissedObservationIds, observationRows],
  );

  const unreadObservationCount = unreadObservationRows.length;

  const dismissObservationFromInbox = (observationId) => {
    if (!teacherUserId) return;
    const next = dismissObservation(teacherUserId, observationId);
    setDismissedObservationIdsState(new Set(next));
  };

  const dismissAllObservationsFromInbox = () => {
    if (!teacherUserId) return;
    const next = new Set(dismissedObservationIds);
    unreadObservationRows.forEach((row) => next.add(String(row.id || "")));
    setDismissedObservationIds(teacherUserId, next);
    setDismissedObservationIdsState(next);
  };

  if (route.name === "kiosk") {
    return <KioskPage channelKey={route.param} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="header-title-row">
            <div className="header-title-copy">
              <h1>Multi-Course District Instructional Planner</h1>
              <p className="subtitle">
                Transform curriculum into interactive day-by-day lesson plans
              </p>
            </div>
            <div className="header-brand-zone">
              <img
                className="brand-logo"
                src={`${process.env.PUBLIC_URL}/brand/primary_logo.png`}
                alt="EPISD Primary Logo"
              />
              <div className="header-brand-lottie" aria-hidden="true">
                <Lottie
                  animationData={meditatingGiraffeAnimation}
                  loop
                  autoplay
                  className="header-brand-lottie-player"
                />
              </div>
            </div>
          </div>
          <div className="top-nav" data-tour="top-nav">
            <button
              className={`nav-btn ${route.name === "planning" ? "active" : ""}`}
              onClick={() => navigate("/planning/week")}
            >
              Planning Hub
            </button>
            <button
              className={`nav-btn ${route.name === "calendar" ? "active" : ""}`}
              onClick={() => navigate("/calendar")}
            >
              Calendar
            </button>
            <button
              className={`nav-btn ${route.name === "guide" ? "active" : ""}`}
              onClick={() => navigate("/guide")}
            >
              Guide
            </button>
            <button
              className={`nav-btn ${route.name === "units" ? "active" : ""}`}
              onClick={() => navigate("/units")}
            >
              Units
            </button>
            <button
              className={`nav-btn ${route.name === "lessons" ? "active" : ""}`}
              onClick={() => navigate("/lessons")}
            >
              Lessons
            </button>
            <button
              className={`nav-btn ${route.name === "teks" ? "active" : ""}`}
              onClick={() => navigate("/teks")}
            >
              TEKS
            </button>
            <button
              className={`nav-btn ${route.name === "performance-tasks" ? "active" : ""}`}
              onClick={() => navigate("/performance-tasks")}
            >
              Tasks
            </button>
            <button
              className={`nav-btn ${route.name === "kiosk-control" ? "active" : ""}`}
              onClick={() => navigate("/kiosk-control")}
            >
              Kiosk Control
            </button>
            {authSession?.accessToken && (
              <button
                className={`nav-btn ${route.name === "profile" ? "active" : ""}`}
                onClick={() => navigate("/profile")}
              >
                Profile
              </button>
            )}
            {isPrincipalOrAdmin && (
              <div className="nav-dropdown" ref={adminDropdownRef}>
                <button
                  className={`nav-btn nav-dropdown-btn ${
                    [
                      "settings",
                      "curriculum",
                      "curriculum-audit",
                      "walkthrough",
                      "user-management",
                    ].includes(route.name)
                      ? "active"
                      : ""
                  }`}
                  onClick={() => setAdminDropdownOpen((o) => !o)}
                  aria-haspopup="true"
                  aria-expanded={adminDropdownOpen}
                >
                  Admin Tools ▾
                </button>
                {adminDropdownOpen && (
                  <div className="nav-dropdown-menu">
                    <button
                      className={`nav-dropdown-item ${route.name === "walkthrough" ? "active" : ""}`}
                      onClick={() => {
                        navigate("/walkthrough");
                        setAdminDropdownOpen(false);
                      }}
                    >
                      Walkthrough
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          className={`nav-dropdown-item ${route.name === "user-management" ? "active" : ""}`}
                          onClick={() => {
                            navigate("/user-management");
                            setAdminDropdownOpen(false);
                          }}
                        >
                          User Management
                        </button>
                        <button
                          className={`nav-dropdown-item ${route.name === "curriculum" ? "active" : ""}`}
                          onClick={() => {
                            navigate("/curriculum");
                            setAdminDropdownOpen(false);
                          }}
                        >
                          Curriculum Hub
                        </button>
                        <button
                          className={`nav-dropdown-item ${route.name === "settings" ? "active" : ""}`}
                          onClick={() => {
                            navigate("/settings");
                            setAdminDropdownOpen(false);
                          }}
                        >
                          Admin Settings
                        </button>
                        <button
                          className={`nav-dropdown-item ${route.name === "curriculum-audit" ? "active" : ""}`}
                          onClick={() => {
                            navigate("/curriculum-audit");
                            setAdminDropdownOpen(false);
                          }}
                        >
                          Curriculum Audit
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="page-header" data-tour="page-header">
          <h2>{pageTitle}</h2>
          <div className="page-header-actions">
            <div className="auth-controls">
              <span className="auth-status-pill">
                {authProfile?.displayName ||
                  authProfile?.email ||
                  "Not signed in"}
              </span>
              {authSession?.accessToken && isTeacher && (
                <button
                  className="observation-bell-btn"
                  type="button"
                  onClick={() => setObservationInboxOpen(true)}
                  aria-label={`Observation notifications (${unreadObservationCount} unread)`}
                  title="Observation notifications"
                >
                  <span aria-hidden="true">🔔</span>
                  {unreadObservationCount > 0 ? (
                    <span className="observation-bell-count">
                      {unreadObservationCount}
                    </span>
                  ) : null}
                </button>
              )}
              {authSession?.accessToken ? (
                <button
                  className="tour-launch-btn"
                  type="button"
                  onClick={handleLogout}
                >
                  Sign Out
                </button>
              ) : (
                <div className="sign-in-cta-wrap">
                  {showSignInChevron && (
                    <div className="sign-in-chevron-hint" aria-hidden="true">
                      <Lottie
                        animationData={signInArrowAnimation}
                        loop
                        autoplay
                        className="sign-in-chevron-player"
                      />
                    </div>
                  )}
                  <button
                    className={`tour-launch-btn ${showSignInNudge ? "sign-in-nudge" : ""}`}
                    type="button"
                    onClick={openLoginModal}
                  >
                    Sign In
                  </button>
                </div>
              )}
            </div>
            <GuidedTour
              routeName={route.name}
              currentCourse={activeCourseSelection}
              onCourseChange={setActiveCourseSelection}
            />
            {route.name === "calendar" && (
              <a
                className="tour-launch-btn district-calendar-btn"
                href={`${process.env.PUBLIC_URL}/2025-26_EPISD_Calendar_Approved.pdf`}
                target="_blank"
                rel="noopener noreferrer"
              >
                District Calendar
              </a>
            )}
          </div>
        </div>

        {!isRecoveryRoute &&
          route.name !== "curriculum" &&
          route.name !== "profile" &&
          !(route.name === "planning" && route.param === "observations") && (
            <CourseSwitcher
              currentCourse={activeCourseSelection}
              onCourseChange={setActiveCourseSelection}
              showQuickSelect={route.name === "calendar"}
              enablePrivateObjectiveToggle={route.name === "kiosk-control"}
              enableMyClassesOnlyFilter={route.name === "planning" && route.param === "week"}
              authProfile={authProfile}
            />
          )}

        {route.name === "calendar" && (
          <CalendarPage
            courseId={currentCourse}
            onOpenLesson={(lessonId) => navigate(`/lesson/${lessonId}`)}
          />
        )}
        {route.name === "units" && <UnitsPage courseId={currentCourse} />}
        {route.name === "planning" && (
          <PlanningHubPage
            courseId={currentCourse}
            currentCourse={currentCourse}
            authProfile={authProfile}
            activeTab={route.param || "week"}
            onNavigateTab={(tab) => navigate(`/planning/${tab}`)}
            onOpenLesson={(lessonId) => navigate(`/lesson/${lessonId}`)}
          />
        )}
        {route.name === "kiosk-control" && (
          <KioskControlPage courseId={kioskCourseSelection} />
        )}
        {route.name === "guide" && (
          <GuidePage
            courseId={currentCourse}
            onOpenLesson={(lessonId) => navigate(`/lesson/${lessonId}`)}
          />
        )}
        {route.name === "lessons" && (
          <LessonLibraryPage
            courseId={currentCourse}
            onOpenLesson={(lessonId) => navigate(`/lesson/${lessonId}`)}
          />
        )}
        {route.name === "teks" && <TeksPage courseId={currentCourse} />}
        {route.name === "forgot-password" && (
          <ForgotPasswordPage
            onOpenSignIn={() => {
              navigate("/calendar");
              openLoginModal();
            }}
          />
        )}
        {route.name === "reset-password" && (
          <ResetPasswordPage
            onResetComplete={handlePasswordResetComplete}
            onRequestResetLink={() => navigate("/forgot-password")}
          />
        )}
        {route.name === "settings" && isAdmin && <SettingsPage />}
        {route.name === "settings" && !isAdmin && (
          <div className="panel-card">
            <p className="admin-error">
              Admin access is required to view this page.
            </p>
          </div>
        )}
        {route.name === "curriculum" && isAdmin && (
          <CurriculumPage authProfile={authProfile} />
        )}
        {route.name === "curriculum" && !isAdmin && (
          <div className="panel-card">
            <p className="admin-error">
              Admin access is required to view this page.
            </p>
          </div>
        )}
        {route.name === "performance-tasks" && (
          <PerformanceTasksPage taskId={route.param} courseId={currentCourse} />
        )}
        {route.name === "curriculum-audit" && (
          <CurriculumAuditPage courseId={currentCourse} />
        )}
        {route.name === "walkthrough" && isPrincipalOrAdmin && (
          <WalkthroughPage authProfile={authProfile} />
        )}
        {route.name === "walkthrough" && !isPrincipalOrAdmin && (
          <div className="panel-card">
            <p className="admin-error">
              Principal or admin access is required to view this page.
            </p>
          </div>
        )}
        {route.name === "user-management" && isAdmin && (
          <UserManagementPage authProfile={authProfile} />
        )}
        {route.name === "user-management" && !isAdmin && (
          <div className="panel-card">
            <p className="admin-error">
              Admin access is required to view this page.
            </p>
          </div>
        )}
        {route.name === "profile" && authSession?.accessToken && (
          <ProfilePage
            authProfile={authProfile}
            onProfileUpdated={(updated) =>
              setAuthProfile((prev) => ({
                ...(prev || {}),
                ...(updated || {}),
              }))
            }
          />
        )}
        {route.name === "profile" && !authSession?.accessToken && (
          <div className="panel-card">
            <p className="admin-error">Sign in to view your profile.</p>
            <button
              className="tour-launch-btn"
              type="button"
              onClick={openLoginModal}
              style={{ marginTop: 10 }}
            >
              Sign In
            </button>
          </div>
        )}
        {route.name === "lesson" && route.param && (
          <DeepDiveWorkspace
            lessonId={route.param}
            onNavigateLesson={(lessonId) => navigate(`/lesson/${lessonId}`)}
            onClose={() => {
              const target =
                lastNonLessonPath &&
                !String(lastNonLessonPath).startsWith("/lesson/")
                  ? lastNonLessonPath
                  : "/calendar";
              navigate(target);
            }}
          />
        )}
      </main>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
        onForgotPassword={openForgotPasswordPage}
      />

      <WelcomeModal
        isOpen={isWelcomeModalOpen}
        isAdmin={isAdmin}
        onClose={closeWelcomeModal}
      />

      {observationInboxOpen && isTeacher && (
        <div
          className="observation-inbox-overlay"
          role="presentation"
          onClick={() => setObservationInboxOpen(false)}
        >
          <div
            className="observation-inbox-modal"
            role="dialog"
            aria-modal="true"
            aria-label="New principal observation notes"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="observation-inbox-head">
              <div>
                <h3>Observation Notes</h3>
                <p>New notes shared by principals.</p>
              </div>
              <button
                type="button"
                className="nav-lite-btn"
                onClick={() => setObservationInboxOpen(false)}
              >
                Close
              </button>
            </div>

            {observationInboxLoading ? (
              <p className="muted">Loading notes…</p>
            ) : null}
            {!observationInboxLoading && observationInboxError ? (
              <p className="observation-inbox-error">{observationInboxError}</p>
            ) : null}
            {!observationInboxLoading &&
            !observationInboxError &&
            unreadObservationRows.length === 0 ? (
              <p className="muted">No new shared observation notes.</p>
            ) : null}

            {!observationInboxLoading &&
            !observationInboxError &&
            unreadObservationRows.length > 0 ? (
              <>
                <div className="observation-inbox-list">
                  {unreadObservationRows.map((row) => (
                    <article key={row.id} className="observation-inbox-item">
                      <div className="observation-inbox-item-head">
                        <strong>
                          {toObservationDateLabel(row.observationDate)}
                        </strong>
                        <span>{row.principalName || "Principal"}</span>
                      </div>
                      <p>{row.notes}</p>
                      <div className="observation-inbox-item-actions">
                        <button
                          type="button"
                          className="nav-lite-btn"
                          onClick={() => dismissObservationFromInbox(row.id)}
                        >
                          Dismiss
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
                <div className="observation-inbox-foot">
                  <button
                    type="button"
                    className="nav-lite-btn"
                    onClick={dismissAllObservationsFromInbox}
                  >
                    Dismiss All
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => {
                      setObservationInboxOpen(false);
                      navigate("/planning/observations");
                    }}
                  >
                    View Full History
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {authError ? <div className="auth-error-toast">{authError}</div> : null}

      <footer className="app-footer">
        <p>
          © {new Date().getFullYear()} David Hohnholt. Built for EPISD teachers
          and curriculum planning use.
        </p>
        <button
          type="button"
          className="app-version app-version-btn"
          onClick={() => setIsWelcomeModalOpen(true)}
          title="Open welcome overview"
          aria-label="Open welcome overview"
        >
          Version {appVersion.version}
        </button>
      </footer>
    </div>
  );
}

export default App;
